import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type EtiquetaPlantilla } from '../api';
import { mensajeDeError, useApi } from '../lib/useApi';
import { Cargando, Encabezado, Icono, PanelError, useConfirmar, Vacio } from '../components/ui';
import Etiqueta from '../components/Etiqueta';
import Disenador from './etiquetas/Disenador';
import { abrir, aparecer, fila, resorte } from '../components/movimiento';
import { altoMinimo, aviso, botonPrimario, botonSecundario, c, campo, etiquetaCampo, tarjeta, HUSO } from '../theme';

/**
 * Etiquetas del depósito: la lista de plantillas y la puerta al diseñador.
 *
 * Una etiqueta no se guarda como imagen sino como la estructura de lo que hay
 * que dibujar, en milímetros y con las variables sin resolver: así la misma
 * plantilla sirve para todos los capachos y cambiar el formato es cambiarla en
 * un solo lugar.
 *
 * Todo el panel es de administradores. Una etiqueta mal hecha se pega a la
 * mercadería y se va del depósito.
 */

// ---------------------------------------------------------------- la lista

function Lista({
  etiquetas,
  onAbrir,
  onCrear,
  onAccion,
}: {
  etiquetas: EtiquetaPlantilla[];
  onAbrir: (e: EtiquetaPlantilla) => void;
  onCrear: (nombre: string, ancho: number, alto: number) => void;
  onAccion: (que: 'duplicar' | 'predeterminada' | 'borrar' | 'activar', e: EtiquetaPlantilla) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [medida, setMedida] = useState({ ancho: '100', alto: '150' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...tarjeta, padding: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="min-w-0 basis-full tablet:min-w-[180px] tablet:flex-1 tablet:basis-0" style={etiquetaCampo}>
          Etiqueta nueva
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Etiqueta de capacho"
            style={campo}
          />
        </label>
        <label className="min-w-0 flex-1 tablet:w-24 tablet:flex-none" style={etiquetaCampo}>
          Ancho (mm)
          <input
            type="number"
            inputMode="numeric"
            step="1"
            value={medida.ancho}
            onChange={(e) => setMedida({ ...medida, ancho: e.target.value })}
            style={campo}
          />
        </label>
        <label className="min-w-0 flex-1 tablet:w-24 tablet:flex-none" style={etiquetaCampo}>
          Alto (mm)
          <input
            type="number"
            inputMode="numeric"
            step="1"
            value={medida.alto}
            onChange={(e) => setMedida({ ...medida, alto: e.target.value })}
            style={campo}
          />
        </label>
        <button
          type="button"
          onClick={() => onCrear(nombre.trim(), Number(medida.ancho), Number(medida.alto))}
          disabled={!nombre.trim()}
          className="w-full justify-center tablet:w-auto"
          style={{ ...botonPrimario, minHeight: altoMinimo(42), opacity: nombre.trim() ? 1 : 0.6 }}
        >
          Crear y diseñar
        </button>
      </div>

      {etiquetas.length === 0 ? (
        <Vacio
          icono="label"
          titulo="Todavía no hay etiquetas"
          detalle="Creá la primera y dibujala: es la que se va a imprimir para los capachos."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16, position: 'relative' }}>
          <AnimatePresence initial={false} mode="popLayout">
          {etiquetas.map((e, i) => (
            <motion.div key={e.id} layout {...fila(i)} transition={{ ...fila(i).transition, layout: resorte }} style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>{e.nombre}</span>
                  <div style={{ fontSize: 12, color: c.texto3 }}>
                    {e.uso === 'pallet' ? 'Pallet' : 'Capacho'} · {e.ancho} × {e.alto} mm
                    {e.orientacion === 'apaisada' && ' · apaisada'} · v{e.version}
                    {e.plantilla.elementos.length === 0 && ' · sin dibujar'}
                  </div>
                </span>
                <AnimatePresence initial={false}>
                {e.predeterminada && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.28 }}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: c.verdeSuave,
                      border: `1px solid ${c.verde}`,
                      color: c.verde,
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    En uso
                  </motion.span>
                )}
                </AnimatePresence>
              </div>

              {/* Una miniatura de verdad: es la plantilla dibujada, no un ícono */}
              <div
                style={{
                  border: `1px solid ${c.borde}`,
                  borderRadius: 8,
                  background: '#fff',
                  height: 120,
                  display: 'grid',
                  placeItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <Etiqueta
                  plantilla={e.plantilla}
                  ancho={e.ancho}
                  alto={e.alto}
                  orientacion={e.orientacion}
                  datos={{}}
                  style={{ width: 'auto', height: '100%', maxWidth: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => onAbrir(e)} style={{ ...botonSecundario, flex: 1, minHeight: altoMinimo(36) }}>
                  Diseñar
                </button>
                <button
                  type="button"
                  onClick={() => onAccion('duplicar', e)}
                  title="Duplicar"
                  aria-label={`Duplicar ${e.nombre}`}
                  style={{ ...botonSecundario, width: 38, minHeight: altoMinimo(36), padding: 0, display: 'grid', placeItems: 'center' }}
                >
                  <Icono nombre="content_copy" size={16} color={c.texto3} />
                </button>
                <button
                  type="button"
                  onClick={() => onAccion('borrar', e)}
                  title="Borrar"
                  aria-label={`Borrar ${e.nombre}`}
                  style={{ ...botonSecundario, width: 38, minHeight: altoMinimo(36), padding: 0, display: 'grid', placeItems: 'center' }}
                >
                  <Icono nombre="delete" size={16} color={c.rojo} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 12 }}>
                <AnimatePresence initial={false}>
                {!e.predeterminada && (
                  <motion.button
                    {...aparecer}
                    type="button"
                    onClick={() => onAccion('predeterminada', e)}
                    style={{ ...botonSecundario, flex: 1, minHeight: altoMinimo(32), fontSize: 12 }}
                  >
                    Usar esta
                  </motion.button>
                )}
                </AnimatePresence>
                <button
                  type="button"
                  onClick={() => onAccion('activar', e)}
                  style={{ ...botonSecundario, flex: 1, minHeight: altoMinimo(32), fontSize: 12 }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={e.activa ? 'des' : 'act'}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.14 }}
                      style={{ display: 'inline-block' }}
                    >
                      {e.activa ? 'Desactivar' : 'Activar'}
                    </motion.span>
                  </AnimatePresence>
                </button>
              </div>

              {e.actualizadaPor && (
                <div style={{ fontSize: 12, color: c.texto4 }}>
                  Último cambio: {e.actualizadaPor} · {new Date(e.actualizadaEn).toLocaleDateString('es-AR', { timeZone: HUSO })}
                </div>
              )}
            </motion.div>
          ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default function Etiquetas() {
  const { datos, cargando, error, recargar } = useApi(
    async () => {
      const [catalogo, lista] = await Promise.all([api.catalogoEtiquetas(), api.etiquetas()]);
      return { catalogo, etiquetas: lista.etiquetas };
    },
    [],
  );

  const [editando, setEditando] = useState<number | null>(null);
  const [avisoTexto, setAvisoTexto] = useState<{ texto: string; ok: boolean } | null>(null);
  const [confirmar, dialogo] = useConfirmar();

  async function accion(fn: () => Promise<unknown>, exito?: string) {
    setAvisoTexto(null);
    try {
      await fn();
      if (exito) setAvisoTexto({ texto: exito, ok: true });
      recargar();
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
    }
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Buscando las etiquetas…" />;
  if (!datos) return null;

  const abierta = datos.etiquetas.find((e) => e.id === editando) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <AnimatePresence mode="wait" initial={false}>
      {abierta ? (
        // El diseñador se abre desde el centro, como la tarjeta que lo pidió.
        <motion.div key="disenador" {...abrir(16)}>
          <Disenador
            etiqueta={abierta}
            catalogo={datos.catalogo}
            onVolver={() => {
              setEditando(null);
              recargar();
            }}
          />
        </motion.div>
      ) : (
        <motion.div key="lista" {...aparecer} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Encabezado
            bajada={`Diseño de las etiquetas que se imprimen en el depósito. Impresora: ${datos.catalogo.impresora.nombre}, ${datos.catalogo.impresora.dpi} dpi.`}
          />

          <AnimatePresence>
            {avisoTexto && (
              <motion.div
                key={avisoTexto.texto}
                role={avisoTexto.ok ? 'status' : 'alert'}
                {...abrir(8)}
                style={aviso(avisoTexto.ok ? 'ok' : 'err')}
              >
                {avisoTexto.texto}
              </motion.div>
            )}
          </AnimatePresence>

          <Lista
            etiquetas={datos.etiquetas}
            onAbrir={(e) => setEditando(e.id)}
            onCrear={(nombre, ancho, alto) =>
              accion(async () => {
                const creada = await api.crearEtiqueta({ nombre, ancho, alto });
                setEditando(creada.id);
              })
            }
            onAccion={(que, e) => {
              if (que === 'duplicar') return accion(() => api.duplicarEtiqueta(e.id), `Se copió «${e.nombre}».`);
              if (que === 'predeterminada')
                return accion(() => api.etiquetaPredeterminada(e.id), `«${e.nombre}» es la que se va a imprimir.`);
              if (que === 'activar')
                return accion(
                  () => api.guardarEtiqueta(e.id, { activa: !e.activa }),
                  e.activa ? `«${e.nombre}» quedó desactivada.` : `«${e.nombre}» quedó activa.`,
                );
              // La que está en uso el backend no la deja borrar: se avisa sin
              // preguntar algo que igual va a fallar.
              if (e.predeterminada)
                return setAvisoTexto({
                  texto: `«${e.nombre}» es la que se imprime hoy: elegí otra con «Usar esta» antes de borrarla.`,
                  ok: false,
                });
              void (async () => {
                const ok = await confirmar({
                  titulo: `¿Borrar la plantilla «${e.nombre}»?`,
                  detalle: 'Se pierde su diseño. Si sólo querés que no se use, desactivala.',
                  boton: 'Borrar la plantilla',
                });
                if (ok) await accion(() => api.borrarEtiqueta(e.id), `Se borró «${e.nombre}».`);
              })();
            }}
          />
        </motion.div>
      )}
      </AnimatePresence>
      {dialogo}
    </div>
  );
}

